import { css } from '@emotion/react';
import { FontSize } from './vendor/_rfs.mixin';
import { BorderRadius } from './mixins/_border-radius.mixin';
import { BoxShadow } from './mixins/_box-shadow.mixin';
import { Transition } from './mixins/_transition.mixin';
import { FormControlFocus, FormValidationState } from './mixins/_forms.mixin';
import { add } from './_functions';
import { MediaBreakpointUp } from './mixins/_breakpoints.mixin';

export default $ => {
  const rules = [];

  rules.push(css`
    .form-control {
      display: block;
      width: 100%;
      height: ${$.inputHeight};
      padding: ${$.inputPaddingY} ${$.inputPaddingX};
      font-family: ${$.fontFamily};
      ${new FontSize($.inputFontSize).include($)};
      font-weight: ${$.inputFontWeight};
      line-height: ${$.inputLineHeight};
      color: ${$.inputColor};
      background-color: ${$.inputBg};
      background-clip: padding-box;
      border: ${$.inputBorderWidth} solid ${$.inputBorderColor};
      ${new BorderRadius($.inputBorderRadius, 0).include($)};
      ${new BoxShadow($.inputBoxShadow).include($)};
      ${new Transition($.inputTransition).include($)};

      &::-ms-expand {
        background-color: transparent;
        border: 0;
      }

      ${new FormControlFocus(true).include($)};

      &::placeholder {
        color: ${$.inputPlaceholderColor};
        opacity: 1;
      }

      &:disabled,
      &[readonly] {
        background-color: ${$.inputDisabledBg};
        opacity: 1;
      }
    }
  `);

  rules.push(css`
    input[type='date'],
    input[type='time'],
    input[type='datetime-local'],
    input[type='month'] {
      &.form-control {
        appearance: none;
      }
    }
  `);

  rules.push(css`
    select.form-control {
      &:-moz-focusring {
        color: transparent;
        text-shadow: 0 0 0 ${$.inputColor};
      }

      &:focus::-ms-value {
        color: ${$.inputColor};
        background-color: ${$.inputBg};
      }
    }
  `);

  rules.push(css`
    .form-control-file,
    .form-control-range {
      display: block;
      width: 100%;
    }
  `);

  rules.push(css`
    .col-form-label {
      padding-top: ${add($.inputPaddingY, $.inputBorderWidth)};
      padding-bottom: ${add($.inputPaddingY, $.inputBorderWidth)};
      margin-bottom: 0;
      ${new FontSize('inherit').include($)};
      line-height: ${$.inputLineHeight};
    }
  `);

  rules.push(css`
    .col-form-label-lg {
      padding-top: ${add($.inputPaddingYLg, $.inputBorderWidth)};
      padding-bottom: ${add($.inputPaddingYLg, $.inputBorderWidth)};
      ${new FontSize($.inputFontSizeLg).include($)};
      line-height: ${$.inputLineHeightLg};
    }
  `);

  rules.push(css`
    .col-form-label-sm {
      padding-top: ${add($.inputPaddingYSm, $.inputBorderWidth)};
      padding-bottom: ${add($.inputPaddingYSm, $.inputBorderWidth)};
      ${new FontSize($.inputFontSizeSm).include($)};
      line-height: ${$.inputLineHeightSm};
    }
  `);

  rules.push(css`
    .form-control-plaintext {
      display: block;
      width: 100%;
      padding: ${$.inputPaddingY} 0;
      margin-bottom: 0;
      ${new FontSize($.inputFontSize).include($)};
      line-height: ${$.inputLineHeight};
      color: ${$.inputPlaintextColor};
      background-color: transparent;
      border: solid transparent;
      border-width: ${$.inputBorderWidth} 0;

      &.form-control-sm,
      &.form-control-lg {
        padding-right: 0;
        padding-left: 0;
      }
    }
  `);

  rules.push(css`
    .form-control-sm {
      height: ${$.inputHeightSm};
      padding: ${$.inputPaddingYSm} ${$.inputPaddingXSm};
      ${new FontSize($.inputFontSizeSm).include($)};
      line-height: ${$.inputLineHeightSm};
      ${new BorderRadius($.inputBorderRadiusSm).include($)};
    }
  `);

  rules.push(css`
    .form-control-lg {
      height: ${$.inputHeightLg};
      padding: ${$.inputPaddingYLg} ${$.inputPaddingXLg};
      ${new FontSize($.inputFontSizeLg).include($)};
      line-height: ${$.inputLineHeightLg};
      ${new BorderRadius($.inputBorderRadiusLg).include($)};
    }
  `);

  rules.push(css`
    select.form-control {
      &[size],
      &[multiple] {
        height: auto;
      }
    }
  `);

  rules.push(css`
    textarea.form-control {
      height: auto;
    }
  `);

  rules.push(css`
    .form-text {
      display: block;
      margin-top: ${$.formTextMarginTop};
    }
  `);

  rules.push(css`
    .form-row {
      display: flex;
      flex-wrap: wrap;
      margin-right: calc(${$.formGridGutterWidth} * -0.5);
      margin-left: calc(${$.formGridGutterWidth} * -0.5);

      > .col,
      > [class*='col-'] {
        padding-right: calc(${$.formGridGutterWidth} * 0.5);
        padding-left: calc(${$.formGridGutterWidth} * 0.5);
      }
    }
  `);

  rules.push(css`
    .form-check {
      position: relative;
      display: block;
      padding-left: ${$.formCheckInputGutter};
    }
  `);

  rules.push(css`
    .form-check-input {
      position: absolute;
      margin-top: ${$.formCheckInputMarginY};
      margin-left: calc(-1 * ${$.formCheckInputGutter});

      &[disabled] ~ .form-check-label,
      &:disabled ~ .form-check-label {
        color: ${$.textMuted};
      }
    }
  `);

  rules.push(css`
    .form-check-label {
      margin-bottom: 0;
    }
  `);

  rules.push(css`
    .form-check-inline {
      display: inline-flex;
      align-items: center;
      padding-left: 0;
      margin-right: ${$.formCheckInlineMarginX};

      .form-check-input {
        position: static;
        margin-top: 0;
        margin-right: ${$.formCheckInlineInputMarginX};
        margin-left: 0;
      }
    }
  `);

  Object.entries($.formValidationStates).forEach(([state, { color, icon }]) => rules.push(new FormValidationState(state, color, icon).include($)));

  rules.push(css`
    .form-inline {
      display: flex;
      flex-flow: row wrap;
      align-items: center;

      .form-check {
        width: 100%;
      }

      ${new MediaBreakpointUp('sm').include(
        $,
        css`
          label {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 0;
          }

          .form-group {
            display: flex;
            flex-flow: row wrap;
            align-items: center;
            flex: 0 0 auto;
            margin-bottom: 0;
          }

          .form-control {
            display: inline-block;
            width: auto;
            vertical-align: middle;
          }

          .form-control-plaintext {
            display: inline-block;
          }

          .input-group,
          .custom-select {
            width: auto;
          }

          .form-check {
            display: flex;
            align-items: center;
            justify-content: center;
            width: auto;
            padding-left: 0;
          }

          .form-check-input {
            position: relative;
            flex-shrink: 0;
            margin-top: 0;
            margin-right: ${$.formCheckInputMarginX};
            margin-left: 0;
          }

          .custom-control {
            align-items: center;
            justify-content: center;
          }

          .custom-control-label {
            margin-bottom: 0;
          }
        `
      )}
    }
  `);

  return css(rules);
};
